import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import {
  FLOW_REPLY_POLICY_REQUIRING_CONFIRMATION,
  FLOW_WARNING_CODES,
  type FlowActiveVersion,
  type FlowCondition,
  type FlowDraft,
  type FlowEdge,
  type FlowNode,
  type FlowValidationCode,
  type FlowVersion,
} from "./contracts";
import {
  flowDraftBodySchema,
  normalizeFlowDraft,
  normalizeFunnelStructureType,
} from "./schema";
import {
  buildFlowDependencyCatalog,
  validateFlowDraft,
  type FlowCatalogSource,
  type ValidateFlowDraftOptions,
} from "./validator";
import {
  compileFlowDraft,
  compileFlowVersionCandidate,
  type CompileFlowResult,
} from "./compiler";
import type { FlowValidationResult } from "./contracts";

/**
 * Comandos locais de rascunho de flow.
 *
 * Eles são a única fronteira de escrita da feature e usam `CommandContext`,
 * `CommandResult`, revisão otimista, fingerprint, auditoria e recibo já
 * compartilhados pelo projeto. A publicação local produz somente uma versão
 * imutável e troca a referência ativa para futuras inscrições. Inscrição,
 * execução, mudança de etapa, tarefa, mensagem e IA continuam inexistentes.
 */

export interface FlowCommandState extends FlowCatalogSource {
  flowDrafts: FlowDraft[];
  activeFlowVersions: FlowActiveVersion[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
}

export interface FlowCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface FlowExecution<T> {
  result: CommandResult<T>;
  state: FlowCommandState;
  persist: boolean;
}

function cloneState(state: FlowCommandState): FlowCommandState {
  return {
    ...state,
    flowDrafts: [...state.flowDrafts],
    flowVersions: [...state.flowVersions],
    activeFlowVersions: [...state.activeFlowVersions],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
  };
}

function failure<T>(
  state: FlowCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
  options: { retryable?: boolean } = {},
): FlowExecution<T> {
  return {
    state,
    persist: false,
    result: commandError(context, code, message, {
      retryable: options.retryable ?? false,
      severity: code === "persistence_error" ? "error" : "warning",
    }) as CommandResult<T>,
  };
}

type RuntimeRecord = Record<string, unknown>;

function asRecord(value: unknown): RuntimeRecord | undefined {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized[key] = descriptor.value;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function denseArray(value: unknown): unknown[] | undefined {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    )
      return undefined;
    const length = lengthDescriptor.value;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== length + 1 || !ownNames.includes("length")) return undefined;
    for (const key of ownNames) {
      if (key === "length") continue;
      if (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= length) return undefined;
    }

    const normalized: unknown[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized.push(descriptor.value);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

const INVALID_FLOW_INPUT = Symbol("invalid_flow_input");

function normalizeJsonValue(value: unknown, seen: WeakSet<object>): unknown | typeof INVALID_FLOW_INPUT {
  try {
    if (value === null) return null;
    if (typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" || typeof value === "bigint"
        ? INVALID_FLOW_INPUT
        : value;
    }
    if (seen.has(value)) return INVALID_FLOW_INPUT;
    seen.add(value);

    if (Array.isArray(value)) {
      const array = denseArray(value);
      if (!array) return INVALID_FLOW_INPUT;
      const normalized: unknown[] = [];
      for (const item of array) {
        const child = normalizeJsonValue(item, seen);
        if (child === INVALID_FLOW_INPUT) return INVALID_FLOW_INPUT;
        normalized.push(child);
      }
      seen.delete(value);
      return normalized;
    }

    const record = asRecord(value);
    if (!record) return INVALID_FLOW_INPUT;
    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(record)) {
      const child = normalizeJsonValue(record[key], seen);
      if (child === INVALID_FLOW_INPUT) return INVALID_FLOW_INPUT;
      normalized[key] = child;
    }
    seen.delete(value);
    return normalized;
  } catch {
    return INVALID_FLOW_INPUT;
  }
}

function normalizeFlowJson(value: unknown): unknown | undefined {
  const normalized = normalizeJsonValue(value, new WeakSet<object>());
  return normalized === INVALID_FLOW_INPUT ? undefined : normalized;
}

function checkedContext(context: unknown): CommandContext | undefined {
  const normalized = normalizeFlowJson(context);
  if (normalized === undefined) return undefined;
  const parsed = commandContextSchema.safeParse(normalized);
  return parsed.success ? parsed.data : undefined;
}

const INVALID_FLOW_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "flow-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

function invalidContextFailure<T>(state: FlowCommandState): FlowExecution<T> {
  return {
    state,
    persist: false,
    result: commandError(INVALID_FLOW_CONTEXT, "validation_error", "Contexto de comando inválido.", {
      severity: "warning",
    }) as CommandResult<T>,
  };
}

function replayLookup<T>(
  state: FlowCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): FlowExecution<T> | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return failure<T>(
      state,
      context,
      "idempotency_conflict",
      "A chave de idempotência já foi usada para outra intenção.",
    );
  if (!receipt.result.ok) return { state, persist: false, result: receipt.result as CommandResult<T> };
  return {
    state,
    persist: false,
    result: { ...receipt.result, status: "replayed" } as CommandSuccess<T>,
  };
}

interface CommitInput<T> {
  state: FlowCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  targetId: string;
  targetType?: string;
  value: T;
  version: number;
  warnings?: string[];
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  deps: FlowCommandDeps;
  occurredAt?: string;
}

function commit<T>(input: CommitInput<T>): FlowExecution<T> {
  const occurredAt = input.occurredAt ?? input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<T> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: input.version,
    eventIds: [],
    // Definição de flow não produz efeito externo: nenhum job é enfileirado.
    outboxJobIds: [],
    auditId,
    warnings: input.warnings ?? [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: input.targetType ?? "flow_draft",
    targetId: input.targetId,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied",
    occurredAt,
    expectedVersion: input.context.expectedVersion,
    nextVersion: input.version,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    persist: true,
    state: {
      ...input.state,
      auditLog: [...input.state.auditLog, audit],
      commandReceipts: [...input.state.commandReceipts, receipt],
    },
  };
}

// ---------------------------------------------------------------------------
// Leitura: validar e compilar sem alterar o rascunho
// ---------------------------------------------------------------------------

export function flowCatalogFromState(state: FlowCommandState) {
  return buildFlowDependencyCatalog({
    ownerId: state.ownerId,
    funnels: state.funnels,
    stages: state.stages,
    leadFields: state.leadFields,
    tags: state.tags,
    triggers: state.triggers,
    lossReasons: state.lossReasons,
    messageTemplateVersions: state.messageTemplateVersions,
    aiPromptVersions: state.aiPromptVersions,
    replyClassificationVersions: state.replyClassificationVersions,
    approachTypeVersions: state.approachTypeVersions,
    flowVersions: state.flowVersions,
    flowDrafts: state.flowDrafts,
  });
}

function funnelModeOf(state: FlowCommandState, funnelId: string) {
  return state.funnels.find((funnel) => funnel.id === funnelId)?.defaultExecutionMode;
}

/** Valida um rascunho sem alterar estado algum. */
export function validateFlowDraftById(
  state: FlowCommandState,
  draftId: string,
  options: ValidateFlowDraftOptions = {},
): FlowValidationResult | null {
  const draft = state.flowDrafts.find(
    (item) => item.id === draftId && item.ownerId === state.ownerId,
  );
  if (!draft) return null;
  return validateFlowDraft(draft, flowCatalogFromState(state), options);
}

/** Compila um rascunho sem alterar estado algum e sem publicar versão. */
export function compileFlowDraftById(
  state: FlowCommandState,
  draftId: string,
  options: ValidateFlowDraftOptions = {},
): CompileFlowResult | null {
  const draft = state.flowDrafts.find(
    (item) => item.id === draftId && item.ownerId === state.ownerId,
  );
  if (!draft) return null;
  return compileFlowDraft(draft, flowCatalogFromState(state), {
    ...options,
    funnelMode: funnelModeOf(state, draft.funnelId),
  });
}

// ---------------------------------------------------------------------------
// Comandos
// ---------------------------------------------------------------------------

export const createFlowDraftSchema = flowDraftBodySchema.extend({
  funnelId: z.string().trim().min(1),
});

export const saveFlowDraftSchema = flowDraftBodySchema.extend({
  draftId: z.string().trim().min(1),
});

export const duplicateFlowDraftSchema = z.object({
  draftId: z.string().trim().min(1),
  name: z.string().trim().min(1).max(160),
});

export const archiveFlowDraftSchema = z.object({
  draftId: z.string().trim().min(1),
  reason: z.string().trim().min(1).max(500),
});

export const restoreFlowDraftSchema = z.object({
  draftId: z.string().trim().min(1),
});

export type CreateFlowDraftInput = z.input<typeof createFlowDraftSchema>;
export type SaveFlowDraftInput = z.input<typeof saveFlowDraftSchema>;
export type DuplicateFlowDraftInput = z.infer<typeof duplicateFlowDraftSchema>;
export type ArchiveFlowDraftInput = z.infer<typeof archiveFlowDraftSchema>;
export type RestoreFlowDraftInput = z.infer<typeof restoreFlowDraftSchema>;

export interface FlowDraftValue {
  draft: FlowDraft;
}

export interface DuplicateFlowDraftValue extends FlowDraftValue {
  sourceDraftId: string;
}

function parseOrFail<T>(
  schema: z.ZodType<T>,
  input: unknown,
  fallbackMessage: string,
): { ok: true; data: T } | { ok: false; message: string } {
  const parsed = schema.safeParse(normalizeFlowJson(input));
  if (parsed.success) return { ok: true, data: parsed.data };
  return { ok: false, message: parsed.error.issues[0]?.message ?? fallbackMessage };
}

function conflictingEntryDraft(
  state: FlowCommandState,
  funnelId: string,
  draftId: string | undefined,
): FlowDraft | undefined {
  return state.flowDrafts.find(
    (item) =>
      item.ownerId === state.ownerId &&
      item.funnelId === funnelId &&
      item.status === "editable" &&
      item.isEntryFlow &&
      item.id !== draftId,
  );
}

function conflictingDraftName(
  state: FlowCommandState,
  funnelId: string,
  name: string,
  draftId?: string,
): FlowDraft | undefined {
  return state.flowDrafts.find(
    (item) =>
      item.ownerId === state.ownerId &&
      item.funnelId === funnelId &&
      item.name === name &&
      item.id !== draftId,
  );
}

function existingSimpleDraft(
  state: FlowCommandState,
  funnelId: string,
  draftId?: string,
): FlowDraft | undefined {
  return state.flowDrafts.find(
    (item) =>
      item.ownerId === state.ownerId &&
      item.funnelId === funnelId &&
      item.structureType === "simple" &&
      item.id !== draftId,
  );
}

function ownerFailure<T>(
  state: FlowCommandState,
  context: CommandContext,
): FlowExecution<T> | null {
  return context.ownerId === state.ownerId
    ? null
    : failure(
        state,
        context,
        "forbidden",
        "O comando não pertence ao proprietário do estado carregado.",
      );
}

export function executeCreateFlowDraft(
  stateInput: FlowCommandState,
  input: CreateFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<FlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const ownership = ownerFailure<FlowDraftValue>(stateInput, context);
  if (ownership) return ownership;
  const command = "flows.draft.create";
  const parsed = parseOrFail(createFlowDraftSchema, input, "Rascunho inválido.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);

  const fingerprint = stableFingerprint(parsed.data);
  const state = cloneState(stateInput);
  const replay = replayLookup<FlowDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const funnel = state.funnels.find((item) => item.id === parsed.data.funnelId);
  if (!funnel)
    return failure(stateInput, context, "not_found", "Funil não encontrado para este proprietário.");
  if (funnel.archived)
    return failure(stateInput, context, "invalid_state", "Funil arquivado não aceita novo rascunho.");
  if (normalizeFunnelStructureType(funnel.structureType) !== parsed.data.structureType)
    return failure(
      stateInput,
      context,
      "validation_error",
      "A estrutura do rascunho precisa corresponder à estrutura persistida do funil.",
    );
  if (
    parsed.data.structureType === "simple" &&
    existingSimpleDraft(state, parsed.data.funnelId)
  )
    return failure(
      stateInput,
      context,
      "invalid_state",
      "O funil simples já possui sua definição linear implícita.",
    );
  if (conflictingDraftName(state, parsed.data.funnelId, parsed.data.name))
    return failure(
      stateInput,
      context,
      "invalid_state",
      "Já existe um rascunho com este nome no funil.",
    );

  if (parsed.data.isEntryFlow && conflictingEntryDraft(state, parsed.data.funnelId, undefined))
    return failure(
      stateInput,
      context,
      "invalid_state",
      "O funil já possui um rascunho marcado como entrada.",
    );

  const now = deps.now();
  let draft: FlowDraft;
  try {
    draft = normalizeFlowDraft({
      ...parsed.data,
      id: deps.uuid(),
      ownerId: context.ownerId,
      funnelId: parsed.data.funnelId,
      revision: 1,
      status: "editable",
      createdAt: now,
      updatedAt: now,
    });
  } catch {
    return failure(stateInput, context, "validation_error", "Rascunho inválido após normalização.");
  }

  return commit<FlowDraftValue>({
    state: { ...state, flowDrafts: [...state.flowDrafts, draft] },
    context,
    command,
    fingerprint,
    targetId: draft.id,
    value: { draft },
    version: draft.revision,
    after: { name: draft.name, structureType: draft.structureType },
    deps,
  });
}

export function executeSaveFlowDraft(
  stateInput: FlowCommandState,
  input: SaveFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<FlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const ownership = ownerFailure<FlowDraftValue>(stateInput, context);
  if (ownership) return ownership;
  const command = "flows.draft.save";
  const parsed = parseOrFail(saveFlowDraftSchema, input, "Rascunho inválido.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);

  const fingerprint = stableFingerprint({
    input: parsed.data,
    expectedVersion: context.expectedVersion,
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<FlowDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.flowDrafts.find(
    (item) => item.id === parsed.data.draftId && item.ownerId === context.ownerId,
  );
  if (!current) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (current.status !== "editable")
    return failure(stateInput, context, "invalid_state", "Rascunho arquivado não pode ser salvo.");
  const funnel = state.funnels.find((item) => item.id === current.funnelId);
  if (!funnel)
    return failure(stateInput, context, "not_found", "Funil do rascunho não encontrado.");
  if (
    parsed.data.structureType !== current.structureType ||
    parsed.data.structureType !== normalizeFunnelStructureType(funnel.structureType)
  )
    return failure(
      stateInput,
      context,
      "validation_error",
      "A estrutura de um rascunho não pode ser alterada no lugar.",
    );
  if (conflictingDraftName(state, current.funnelId, parsed.data.name, current.id))
    return failure(
      stateInput,
      context,
      "invalid_state",
      "Já existe outro rascunho com este nome no funil.",
    );

  // Revisão otimista obrigatória: sem ela, uma resposta antiga sobrescreveria
  // uma alteração mais nova.
  if (context.expectedVersion === undefined)
    return failure(
      stateInput,
      context,
      "validation_error",
      "Salvar um rascunho exige a revisão esperada.",
    );
  if (context.expectedVersion !== current.revision)
    return failure(
      stateInput,
      context,
      "version_conflict",
      `O rascunho está na revisão ${current.revision} e a alteração esperava ${context.expectedVersion}.`,
    );

  if (parsed.data.isEntryFlow && conflictingEntryDraft(state, current.funnelId, current.id))
    return failure(
      stateInput,
      context,
      "invalid_state",
      "O funil já possui outro rascunho marcado como entrada.",
    );

  let draft: FlowDraft;
  try {
    draft = normalizeFlowDraft({
      ...parsed.data,
      id: current.id,
      ownerId: current.ownerId,
      funnelId: current.funnelId,
      revision: current.revision + 1,
      status: current.status,
      createdAt: current.createdAt,
      updatedAt: deps.now(),
    });
  } catch {
    return failure(stateInput, context, "validation_error", "Rascunho inválido após normalização.");
  }

  return commit<FlowDraftValue>({
    state: {
      ...state,
      flowDrafts: state.flowDrafts.map((item) => (item.id === draft.id ? draft : item)),
    },
    context,
    command,
    fingerprint,
    targetId: draft.id,
    value: { draft },
    version: draft.revision,
    before: { revision: current.revision },
    after: { revision: draft.revision },
    deps,
  });
}

// ---------------------------------------------------------------------------
// Publicação local: versão imutável e referência ativa
// ---------------------------------------------------------------------------

export const publishFlowDraftSchema = z.object({
  draftId: z.string().trim().min(1),
  changelog: z.string().trim().min(1).max(2000),
  confirmedWarningCodes: z.array(z.enum(FLOW_WARNING_CODES)).default([]),
});

export type PublishFlowDraftInput = z.input<typeof publishFlowDraftSchema>;

export interface PublishFlowDraftValue {
  version: FlowVersion;
  activeVersion: FlowActiveVersion;
  warningCodes: FlowValidationCode[];
}

function nextFlowVersionNumber(state: FlowCommandState, flowId: string): number {
  return (
    state.flowVersions.reduce(
      (highest, version) =>
        version.ownerId === state.ownerId && version.flowId === flowId
          ? Math.max(highest, version.version)
          : highest,
      0,
    ) + 1
  );
}

/**
 * Publica somente em memória/demo. A operação espelha a futura fronteira
 * transacional: trava semanticamente a revisão esperada, recompila a fonte
 * canônica, fixa dependências, cria uma versão nova e troca a referência ativa
 * no mesmo estado confirmado. Nenhuma run é criada e nenhuma versão antiga é
 * alterada.
 */
export function executePublishFlowDraft(
  stateInput: FlowCommandState,
  input: PublishFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<PublishFlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const ownership = ownerFailure<PublishFlowDraftValue>(stateInput, context);
  if (ownership) return ownership;
  const command = "flows.publish";
  const parsed = parseOrFail(publishFlowDraftSchema, input, "Publicação inválida.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);

  const fingerprint = stableFingerprint({
    input: parsed.data,
    expectedVersion: context.expectedVersion,
  });
  const state = cloneState(stateInput);
  const replay = replayLookup<PublishFlowDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const draft = state.flowDrafts.find(
    (item) => item.id === parsed.data.draftId && item.ownerId === context.ownerId,
  );
  if (!draft) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (draft.status !== "editable")
    return failure(stateInput, context, "invalid_state", "Rascunho arquivado não pode ser publicado.");
  const funnel = state.funnels.find((item) => item.id === draft.funnelId);
  if (!funnel)
    return failure(stateInput, context, "not_found", "Funil do rascunho não encontrado.");
  if (funnel.archived)
    return failure(stateInput, context, "invalid_state", "Funil arquivado não aceita nova publicação.");

  if (context.expectedVersion === undefined)
    return failure(
      stateInput,
      context,
      "validation_error",
      "Publicar exige a revisão esperada do rascunho.",
    );
  if (context.expectedVersion !== draft.revision)
    return failure(
      stateInput,
      context,
      "version_conflict",
      `O rascunho está na revisão ${draft.revision} e a publicação esperava ${context.expectedVersion}.`,
    );

  if (
    state.flowVersions.some(
      (version) =>
        version.ownerId === context.ownerId &&
        version.flowId === draft.id &&
        version.sourceRevision === draft.revision,
    )
  )
    return failure(
      stateInput,
      context,
      "invalid_state",
      "Esta revisão do rascunho já originou uma versão publicada.",
    );

  const occurredAt = deps.now();
  const compiled = compileFlowVersionCandidate(draft, flowCatalogFromState(state), {
    version: nextFlowVersionNumber(state, draft.id),
    changelog: parsed.data.changelog,
    authorId: context.actorId,
    createdAt: occurredAt,
    funnelMode: funnel.defaultExecutionMode,
  });
  if (!compiled.ok) {
    const first = compiled.validation.errors[0];
    return failure(
      stateInput,
      context,
      "validation_error",
      first
        ? `A publicação foi bloqueada por ${first.code}: ${first.message}`
        : "O rascunho não passou na validação de publicação.",
    );
  }

  const warningCodes = compiled.validation.warnings.map((warning) => warning.code);
  if (
    draft.replyPolicy === FLOW_REPLY_POLICY_REQUIRING_CONFIRMATION &&
    !parsed.data.confirmedWarningCodes.includes("flow.warning.reply_continue_and_notify")
  )
    return failure(
      stateInput,
      context,
      "validation_error",
      'Publicar com "continuar e notificar" exige confirmação explícita do aviso.',
    );

  const version: FlowVersion = Object.freeze({
    ...compiled.candidate,
    id: deps.uuid(),
    publishedAt: occurredAt,
  });
  const activeVersion: FlowActiveVersion = Object.freeze({
    ownerId: context.ownerId,
    flowId: version.flowId,
    funnelId: version.funnelId,
    flowVersionId: version.id,
    version: version.version,
    activatedAt: occurredAt,
  });
  const previousActive = state.activeFlowVersions.find(
    (item) => item.ownerId === context.ownerId && item.flowId === version.flowId,
  );
  const displacedEntryVersions = version.snapshot.isEntryFlow
    ? state.activeFlowVersions.filter((reference) => {
        if (
          reference.ownerId !== context.ownerId ||
          reference.funnelId !== version.funnelId ||
          reference.flowId === version.flowId
        )
          return false;
        return state.flowVersions.some(
          (published) =>
            published.ownerId === context.ownerId &&
            published.id === reference.flowVersionId &&
            published.flowId === reference.flowId &&
            published.version === reference.version &&
            published.snapshot.isEntryFlow,
        );
      })
    : [];
  const displacedEntryFlowIds = new Set(displacedEntryVersions.map((item) => item.flowId));
  const nextState: FlowCommandState = {
    ...state,
    flowVersions: [...state.flowVersions, version],
    activeFlowVersions: [
      ...state.activeFlowVersions.filter(
        (item) =>
          !(
            item.ownerId === context.ownerId &&
            (item.flowId === version.flowId || displacedEntryFlowIds.has(item.flowId))
          ),
      ),
      activeVersion,
    ],
  };

  return commit<PublishFlowDraftValue>({
    state: nextState,
    context,
    command,
    fingerprint,
    targetType: "flow_version",
    targetId: version.id,
    value: { version, activeVersion, warningCodes },
    version: version.version,
    warnings: compiled.validation.warnings.map((warning) => warning.message),
    before:
      previousActive || displacedEntryVersions.length
        ? {
            flowVersionId: previousActive?.flowVersionId,
            version: previousActive?.version,
            displacedEntryFlowVersionIds: displacedEntryVersions.map(
              (item) => item.flowVersionId,
            ),
          }
        : undefined,
    after: {
      flowVersionId: version.id,
      version: version.version,
      sourceRevision: version.sourceRevision,
      planHash: version.hash,
    },
    deps,
    occurredAt,
  });
}

function remapCondition(condition: FlowCondition, nodeIds: Map<string, string>): FlowCondition {
  if (condition.type === "comparison") {
    if (condition.operand.kind !== "task_completed") return condition;
    return {
      ...condition,
      operand: {
        kind: "task_completed",
        taskNodeId: nodeIds.get(condition.operand.taskNodeId) ?? condition.operand.taskNodeId,
      },
    };
  }
  if (condition.type === "not")
    return { type: "not", condition: remapCondition(condition.condition, nodeIds) };
  return {
    type: condition.type,
    conditions: condition.conditions.map((item) => remapCondition(item, nodeIds)),
  };
}

export function executeDuplicateFlowDraft(
  stateInput: FlowCommandState,
  input: DuplicateFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<DuplicateFlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const ownership = ownerFailure<DuplicateFlowDraftValue>(stateInput, context);
  if (ownership) return ownership;
  const command = "flows.draft.duplicate";
  const parsed = parseOrFail(duplicateFlowDraftSchema, input, "Duplicação inválida.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);

  const fingerprint = stableFingerprint(parsed.data);
  const state = cloneState(stateInput);
  const replay = replayLookup<DuplicateFlowDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const source = state.flowDrafts.find(
    (item) => item.id === parsed.data.draftId && item.ownerId === context.ownerId,
  );
  if (!source) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (source.structureType === "simple")
    return failure(
      stateInput,
      context,
      "invalid_state",
      "O funil simples possui uma única definição implícita e não pode ser duplicado.",
    );
  if (conflictingDraftName(state, source.funnelId, parsed.data.name))
    return failure(
      stateInput,
      context,
      "invalid_state",
      "Já existe um rascunho com este nome no funil.",
    );

  const nodeIds = new Map(source.nodes.map((node) => [node.id, deps.uuid()] as const));
  const nodes: FlowNode[] = source.nodes.map((node) => ({
    ...node,
    id: nodeIds.get(node.id) as string,
  }));
  const edges: FlowEdge[] = source.edges.map((edge) => ({
    ...edge,
    id: deps.uuid(),
    fromNodeId: nodeIds.get(edge.fromNodeId) ?? edge.fromNodeId,
    toNodeId: nodeIds.get(edge.toNodeId) ?? edge.toNodeId,
    condition: edge.condition ? remapCondition(edge.condition, nodeIds) : undefined,
  }));

  const now = deps.now();
  let draft: FlowDraft;
  try {
    draft = normalizeFlowDraft({
      name: parsed.data.name,
      description: source.description,
      structureType: source.structureType,
      mode: source.mode,
      entryPolicy: source.entryPolicy,
      entryDomainEvent: source.entryDomainEvent,
      reentryPolicy: source.reentryPolicy,
      replyPolicy: source.replyPolicy,
      // A cópia nunca nasce como entrada: isso é decisão explícita posterior.
      isEntryFlow: false,
      nodes,
      edges,
      id: deps.uuid(),
      ownerId: context.ownerId,
      funnelId: source.funnelId,
      revision: 1,
      status: "editable",
      createdAt: now,
      updatedAt: now,
    });
  } catch {
    return failure(stateInput, context, "validation_error", "Cópia inválida após normalização.");
  }

  return commit<DuplicateFlowDraftValue>({
    state: { ...state, flowDrafts: [...state.flowDrafts, draft] },
    context,
    command,
    fingerprint,
    targetId: draft.id,
    value: { draft, sourceDraftId: source.id },
    version: draft.revision,
    after: { sourceDraftId: source.id },
    deps,
  });
}

function executeStatusChange(
  stateInput: FlowCommandState,
  draftId: string,
  target: FlowDraft["status"],
  command: string,
  fingerprint: string,
  context: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<FlowDraftValue> {
  const ownership = ownerFailure<FlowDraftValue>(stateInput, context);
  if (ownership) return ownership;
  const state = cloneState(stateInput);
  const replay = replayLookup<FlowDraftValue>(state, context, command, fingerprint);
  if (replay) return replay;

  const current = state.flowDrafts.find(
    (item) => item.id === draftId && item.ownerId === context.ownerId,
  );
  if (!current) return failure(stateInput, context, "not_found", "Rascunho não encontrado.");
  if (current.status === target)
    return failure(
      stateInput,
      context,
      "invalid_state",
      target === "archived" ? "O rascunho já está arquivado." : "O rascunho já está ativo.",
    );
  if (context.expectedVersion === undefined)
    return failure(
      stateInput,
      context,
      "validation_error",
      "Alterar o status de um rascunho exige a revisão esperada.",
    );
  if (context.expectedVersion !== current.revision)
    return failure(
      stateInput,
      context,
      "version_conflict",
      `O rascunho está na revisão ${current.revision} e a alteração esperava ${context.expectedVersion}.`,
    );

  const now = deps.now();
  const draft: FlowDraft = {
    ...current,
    status: target,
    // Arquivar não apaga versões publicadas: elas permanecem auditáveis.
    archivedAt: target === "archived" ? now : undefined,
    isEntryFlow: target === "archived" ? false : current.isEntryFlow,
    revision: current.revision + 1,
    updatedAt: now,
  };

  return commit<FlowDraftValue>({
    state: {
      ...state,
      flowDrafts: state.flowDrafts.map((item) => (item.id === draft.id ? draft : item)),
    },
    context,
    command,
    fingerprint,
    targetId: draft.id,
    value: { draft },
    version: draft.revision,
    before: { status: current.status, revision: current.revision },
    after: { status: draft.status, revision: draft.revision },
    deps,
  });
}

export function executeArchiveFlowDraft(
  stateInput: FlowCommandState,
  input: ArchiveFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<FlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const parsed = parseOrFail(archiveFlowDraftSchema, input, "Arquivamento inválido.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);
  return executeStatusChange(
    stateInput,
    parsed.data.draftId,
    "archived",
    "flows.draft.archive",
    stableFingerprint({ input: parsed.data, expectedVersion: context.expectedVersion }),
    context,
    deps,
  );
}

export function executeRestoreFlowDraft(
  stateInput: FlowCommandState,
  input: RestoreFlowDraftInput,
  contextInput: CommandContext,
  deps: FlowCommandDeps,
): FlowExecution<FlowDraftValue> {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const parsed = parseOrFail(restoreFlowDraftSchema, input, "Restauração inválida.");
  if (!parsed.ok) return failure(stateInput, context, "validation_error", parsed.message);
  return executeStatusChange(
    stateInput,
    parsed.data.draftId,
    "editable",
    "flows.draft.restore",
    stableFingerprint({ input: parsed.data, expectedVersion: context.expectedVersion }),
    context,
    deps,
  );
}

// ---------------------------------------------------------------------------
// Adapter demo
// ---------------------------------------------------------------------------

export interface FlowDemoAdapter {
  createDraft(input: CreateFlowDraftInput, context: CommandContext): Promise<CommandResult<FlowDraftValue>>;
  saveDraft(input: SaveFlowDraftInput, context: CommandContext): Promise<CommandResult<FlowDraftValue>>;
  publishDraft(
    input: PublishFlowDraftInput,
    context: CommandContext,
  ): Promise<CommandResult<PublishFlowDraftValue>>;
  duplicateDraft(
    input: DuplicateFlowDraftInput,
    context: CommandContext,
  ): Promise<CommandResult<DuplicateFlowDraftValue>>;
  archiveDraft(input: ArchiveFlowDraftInput, context: CommandContext): Promise<CommandResult<FlowDraftValue>>;
  restoreDraft(input: RestoreFlowDraftInput, context: CommandContext): Promise<CommandResult<FlowDraftValue>>;
  /** Leitura pura: não altera o rascunho nem persiste nada. */
  validateDraft(draftId: string, options?: ValidateFlowDraftOptions): FlowValidationResult | null;
  compileDraft(draftId: string, options?: ValidateFlowDraftOptions): CompileFlowResult | null;
  getState(): FlowCommandState;
  syncExternal(state: FlowCommandState): void;
}

/** Adapter local serializado, no mesmo padrão de tarefas, comunicação e IA. */
export function createFlowDemoAdapter(
  initialState: FlowCommandState,
  deps: FlowCommandDeps,
  onCommit?: (state: FlowCommandState) => void,
): FlowDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = <T,>(operation: () => Promise<FlowExecution<T>>): Promise<CommandResult<T>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = await operation();
        if (execution.persist) {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<T>>;
  };

  return {
    createDraft: (input, context) =>
      enqueue(async () => executeCreateFlowDraft(state, input, context, deps)),
    saveDraft: (input, context) =>
      enqueue(async () => executeSaveFlowDraft(state, input, context, deps)),
    publishDraft: (input, context) =>
      enqueue(async () => executePublishFlowDraft(state, input, context, deps)),
    duplicateDraft: (input, context) =>
      enqueue(async () => executeDuplicateFlowDraft(state, input, context, deps)),
    archiveDraft: (input, context) =>
      enqueue(async () => executeArchiveFlowDraft(state, input, context, deps)),
    restoreDraft: (input, context) =>
      enqueue(async () => executeRestoreFlowDraft(state, input, context, deps)),
    validateDraft: (draftId, options) => validateFlowDraftById(state, draftId, options),
    compileDraft: (draftId, options) => compileFlowDraftById(state, draftId, options),
    getState: () => state,
    syncExternal: (nextState) => {
      if (!busy) state = cloneState(nextState);
    },
  };
}
